/**
 * VideoRenderModal — 3-step wizard for submitting a GoPro overlay render job.
 *
 * Step 1: Upload GoPro chapter MP4 files (drag-drop, auto-sorted by filename)
 * Step 2: Layout editor (full-page drag-and-drop overlay placement)
 * Step 3: Submit and track render progress
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { uploadGoproFiles, submitRenderJob, getRenderStatus, cancelRender } from '../../services/api';
import LayoutEditor from './LayoutEditor/LayoutEditor';
import { serializeOverlays } from './LayoutEditor/editorModel';
import styles from './VideoRenderModal.module.css';

const LAPTIME_NAME_PATTERNS = ['distance lap', 'laptime', 'lap time', 'lap_time', 'running lap time'];

function detectLaptimeChannel(channels) {
  // Prefer an explicit per-lap time channel if the logger exports one
  if (channels?.length) {
    for (const pat of LAPTIME_NAME_PATTERNS) {
      const match = channels.find(ch => ch.name.toLowerCase() === pat);
      if (match) return match.name;
    }
  }
  // Fall back to the absolute Time column — VTR uses this with the beacon
  // markers already in the CSV header to display per-lap elapsed time.
  return 'Time';
}

function buildDefaultOverlays(availableChannels) {
  return [
    {
      type: 'laptime',
      position: { x: 0.01, y: 0.01, width: 0.22, height: 0.13 },
      channel: detectLaptimeChannel(availableChannels),
      label: 'LAP',
      show_best: true,
      show_last: true,
      show_delta: true,
      best_color: [255, 215, 0],
    },
    {
      type: 'numeric',
      position: { x: 0.42, y: 0.01, width: 0.12, height: 0.07 },
      channel: 'GPS Speed',
      label: 'Speed',
      unit: 'mph',
      scale: 0.621371,
      decimal_places: 1,
    },
    {
      type: 'track_map',
      position: { x: 0.80, y: 0.01, width: 0.18, height: 0.18 },
      tail_seconds: 2.0,
    },
  ];
}

// Preview/source resolution. The final output is an ABR ladder generated by VTR,
// so this is no longer user-selectable — it only sizes the layout preview frame
// (overlays use fractional coords, so the exact value just sets the 16:9 preview).
const DEFAULT_RESOLUTION = '1080p';

const RENDER_STATUS_LABELS = {
  queued:           'Queued…',
  extracting_gps:   'Extracting GPS…',
  syncing:          'Syncing video to telemetry…',
  precomputing:     'Pre-computing overlays…',
  encoding:         'Encoding video…',
  completed:        'Complete!',
  failed:           'Failed',
  cancelled:        'Cancelled',
  no_job:           'No job',
  unknown:          'Checking…',
};

export default function VideoRenderModal({ sessionId, availableChannels = [], unitPrefs, hasExistingFiles = false, sessionDuration = 0, lastRenderOverlays = null, initialStep = 1, isAdmin = false, onClose, onRenderStarted, onRenderComplete, onRenderCanceled }) {
  const [step, setStep] = useState(initialStep);

  // Admin only. Indexing normally waits for the sync to come out confident;
  // ticking this asserts the sync is correct so the render is indexed regardless.
  // Left unticked by default: a wrong offset attaches telemetry to the wrong
  // frames, and that error is invisible once it is in the index.
  const [assertSyncForIndex, setAssertSyncForIndex] = useState(false);

  // Step 1 state
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const dropRef = useRef(null);

  // Step 2 state (layout editor manages its own overlay state; we hold the result here)
  const [overlays, setOverlays] = useState(() =>
    (lastRenderOverlays ?? buildDefaultOverlays(availableChannels)).map((o, i) => ({ ...o, _id: i }))
  );
  const [syncOffset, setSyncOffset] = useState('');
  const [videoTransform, setVideoTransform] = useState('none');

  // Step 3 state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const pollRef = useRef(null);

  // ── Cancel / dismiss a render (stuck or failed) ─────────────────────────
  const handleCancelRender = useCallback(async () => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setCancelling(true);
    try {
      await cancelRender(sessionId);
    } catch {
      // Even if VTR cancel fails the server clears local state; proceed regardless.
    } finally {
      setCancelling(false);
      onRenderCanceled?.();
      onClose?.();
    }
  }, [sessionId, onRenderCanceled, onClose]);

  // ── Retry a failed render: back to the layout editor to resubmit ────────
  const handleRetry = useCallback(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setJobStatus(null);
    setSubmitError(null);
    setStep(2);
  }, []);

  // ── File selection ──────────────────────────────────────────────────────
  const addFiles = useCallback((incoming) => {
    const mp4s = [...incoming].filter(f => /\.(mp4|mov)$/i.test(f.name));
    setFiles(prev => {
      const combined = [...prev, ...mp4s];
      // Sort by filename so GoPro chapters (GX01…, GX02…) are in order
      combined.sort((a, b) => a.name.localeCompare(b.name));
      return combined;
    });
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleFileInput = useCallback((e) => {
    addFiles(e.target.files);
    e.target.value = '';
  }, [addFiles]);

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const moveFile = (idx, dir) => {
    setFiles(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // ── Step 1 → 2: upload files ────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadStatus('Starting upload…');
    try {
      await uploadGoproFiles(sessionId, files, (pct, detail) => {
        setUploadProgress(pct);
        if (detail?.status === 'retrying') {
          setUploadStatus(
            `Connection interrupted. Retrying ${detail.currentFilename} `
            + `(${detail.attempt}/${detail.maxAttempts})…`,
          );
        } else if (detail?.status === 'finalizing') {
          setUploadStatus(`Finishing ${detail.currentFilename}…`);
        } else if (detail?.status === 'uploading') {
          setUploadStatus(
            `Uploading file ${detail.currentFile} of ${detail.fileCount}: ${detail.currentFilename}`,
          );
        }
      });
      setUploadProgress(null);
      setUploadStatus('');
      // A new source set invalidates any result detected before the user went
      // back to the upload step. LayoutEditor will detect the new set on entry.
      setSyncOffset('');
      setStep(2);
    } catch (e) {
      setUploadError(e.message);
      setUploadProgress(null);
      setUploadStatus('');
    } finally {
      setUploading(false);
    }
  }, [sessionId, files]);

  // ── Layout editor done: receive overlays and submit ────────────────────
  const handleLayoutDone = useCallback(async (editorOverlays) => {
    setOverlays(editorOverlays);
    setSubmitting(true);
    setSubmitError(null);
    const cleanOverlays = serializeOverlays(editorOverlays);
    const submitPayload = { overlays: cleanOverlays, resolution: DEFAULT_RESOLUTION, output_format: 'both' };
    const parsedOffset = parseFloat(syncOffset);
    if (!isNaN(parsedOffset)) submitPayload.sync_offset_sec = parsedOffset;
    if (videoTransform && videoTransform !== 'none') submitPayload.video_transform = videoTransform;
    if (isAdmin && assertSyncForIndex) submitPayload.index_for_search = true;

    try {
      await submitRenderJob(sessionId, submitPayload);
      setStep(3);
      setJobStatus({ status: 'queued', progress_pct: 0 });
      onRenderStarted?.();
      startPolling(6000);
    } catch (e) {
      setSubmitError(e.message);
      // Stay on step 2 (layout editor) so user can retry
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, syncOffset, videoTransform]);

  // ── Polling ─────────────────────────────────────────────────────────────
  const startPolling = useCallback((initialDelayMs = 3000) => {
    if (pollRef.current) clearTimeout(pollRef.current);

    const schedule = (delayMs) => {
      pollRef.current = setTimeout(async () => {
        try {
          const status = await getRenderStatus(sessionId);
          setJobStatus(status);
          if (['completed', 'failed', 'cancelled'].includes(status.status)) {
            pollRef.current = null;
            if (status.status === 'completed' && onRenderComplete) onRenderComplete();
            return;
          }
        } catch {
          // transient error — keep polling
        }
        schedule(3000);
      }, delayMs);
    };

    schedule(initialDelayMs);
  }, [sessionId, onRenderComplete]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // If opened directly on the progress step, start polling immediately
  useEffect(() => {
    if (initialStep === 3) startPolling(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ──────────────────────────────────────────────────────────────

  // Step 2 is the full-screen layout editor — render it outside the modal
  if (step === 2) {
    return (
      <LayoutEditor
        sessionId={sessionId}
        availableChannels={availableChannels}
        unitPrefs={unitPrefs}
        sessionDuration={sessionDuration}
        initialOverlays={overlays}
        resolution={DEFAULT_RESOLUTION}
        syncOffset={syncOffset}
        setSyncOffset={setSyncOffset}
        isAdmin={isAdmin}
        assertSyncForIndex={assertSyncForIndex}
        setAssertSyncForIndex={setAssertSyncForIndex}
        videoTransform={videoTransform}
        setVideoTransform={setVideoTransform}
        submitting={submitting}
        submitError={submitError}
        onBack={() => setStep(1)}
        onDone={handleLayoutDone}
      />
    );
  }

  return (
    <div className={styles.overlay} onClick={e => { if (!uploading && e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-busy={uploading}>
        <div className={styles.header}>
          <h2 className={styles.title}>Add Overlay Video</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close" disabled={uploading}>×</button>
        </div>

        <div className={styles.steps}>
          {[1, 2, 3].map(n => (
            <div key={n} className={`${styles.stepDot} ${step === n ? styles.stepActive : step > n ? styles.stepDone : ''}`}>
              {step > n ? '✓' : n}
            </div>
          ))}
          <div className={styles.stepLabel}>
            {step === 1 ? 'Upload Video Files' : step === 2 ? 'Configure Overlays' : 'Render Progress'}
          </div>
        </div>

        {/* ── Step 1: file upload ── */}
        {step === 1 && (
          <div className={styles.body}>
            {hasExistingFiles && (
              <div className={styles.existingFilesBar}>
                <span>Previously uploaded GoPro files are still on the server.</span>
                <button className={styles.skipBtn} onClick={() => setStep(2)} disabled={uploading}>
                  Use existing files →
                </button>
              </div>
            )}
            <p className={styles.hint}>
              Upload your camera file here.  You can also upload multiple GoPro chapter files.  If the session spans multiple chapters (GX01, GX02…) upload all of them — they will be stitched automatically.
            </p>

            <div
              ref={dropRef}
              className={`${styles.dropzone} ${uploading ? styles.dropzoneDisabled : ''}`}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { if (!uploading) handleDrop(e); else e.preventDefault(); }}
              onClick={() => { if (!uploading) dropRef.current?.querySelector('input')?.click(); }}
            >
              <input type="file" accept=".mp4,.mov,video/mp4,video/quicktime" multiple onChange={handleFileInput} disabled={uploading} style={{ display: 'none' }} />
              {files.length === 0
                ? <span className={styles.dropHint}>Drop MP4/MOV files here or click to browse</span>
                : (
                  <ul className={styles.fileList} onClick={e => e.stopPropagation()}>
                    {files.map((f, i) => (
                      <li key={i} className={styles.fileItem}>
                        <span className={styles.fileName}>{f.name}</span>
                        <span className={styles.fileSize}>{(f.size / 1_048_576).toFixed(0)} MB</span>
                        <button className={styles.moveBtn} onClick={() => moveFile(i, -1)} disabled={uploading || i === 0}>↑</button>
                        <button className={styles.moveBtn} onClick={() => moveFile(i, 1)} disabled={uploading || i === files.length - 1}>↓</button>
                        <button className={styles.removeBtn} onClick={() => removeFile(i)} disabled={uploading}>×</button>
                      </li>
                    ))}
                  </ul>
                )
              }
            </div>

            {uploadProgress != null && (
              <>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className={styles.uploadStatus} aria-live="polite">{uploadStatus} {uploadProgress}%</p>
              </>
            )}
            {uploadError && <p className={styles.error}>{uploadError}</p>}

            <div className={styles.footer}>
              <button className={styles.cancelBtn} onClick={onClose} disabled={uploading}>Cancel</button>
              <button
                className={styles.primaryBtn}
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
              >
                {uploading ? 'Uploading…' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: progress ── */}
        {step === 3 && (
          <div className={styles.body}>
            {!jobStatus ? (
              <p className={styles.hint}>Starting render job…</p>
            ) : (
              <>
                <div className={styles.statusLabel}>
                  {RENDER_STATUS_LABELS[jobStatus.status] ?? jobStatus.status}
                </div>
                {jobStatus.stage_message && (
                  <p className={styles.stageMsg}>{jobStatus.stage_message}</p>
                )}
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${jobStatus.progress_pct}%` }} />
                </div>
                <p className={styles.progressPct}>{jobStatus.progress_pct}%</p>
                {jobStatus.status === 'completed' && (
                  <p className={styles.successMsg}>
                    Render complete! The video panel will now play your overlay video synced with the telemetry traces.
                  </p>
                )}
                {(jobStatus.status === 'failed' || jobStatus.status === 'cancelled') && (
                  <p className={styles.error}>{jobStatus.error || 'Render failed — check server logs.'}</p>
                )}
              </>
            )}
            {(() => {
              const st = jobStatus?.status;
              const isDone     = st === 'completed';
              const isFailed   = st === 'failed' || st === 'cancelled';
              const inProgress = jobStatus && !isDone && !isFailed && st !== 'no_job';
              return (
                <div className={styles.footer}>
                  {isFailed && (
                    <button className={styles.cancelBtn} onClick={handleRetry}>↻ Retry</button>
                  )}
                  {inProgress && (
                    <button className={styles.cancelBtn} onClick={handleCancelRender} disabled={cancelling}>
                      {cancelling ? 'Cancelling…' : 'Cancel render'}
                    </button>
                  )}
                  <button className={styles.primaryBtn} onClick={isFailed ? handleCancelRender : onClose} disabled={cancelling}>
                    {isDone ? 'Close'
                      : isFailed ? 'Dismiss'
                      : 'Close (render continues in background)'}
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </div>

    </div>
  );
}
